/**
 * Puts a version's backing (music, duet partner, AI vocal) on engine channels for "Sing it myself"
 * and mixes what you hear: the chosen backing + an optional quiet AI guide vocal.
 */
import { useEffect, useRef, useState } from "react";
import { loadUrlIntoChannel } from "@/lib/creatorApi";

const GUIDE_VOLUME = 22;
const FULL_VOLUME = 80;

async function ensureChannel(engine, url, name) {
  const existing = engine.channels.findIndex((c) => c?.buffer && c.sourceUrl === url);
  if (existing >= 0) return existing;
  const [idx] = engine.freeChannelIndices(1);
  if (idx == null) throw new Error("The studio is full — clear a channel in the Advanced Studio first.");
  await loadUrlIntoChannel(engine, idx, url, name, url);
  return idx;
}

export default function useSingSession({ engine, backing, title, mode, guide }) {
  const [state, setState] = useState("idle");
  const [error, setError] = useState(null);
  const [channels, setChannels] = useState({});
  const mutedByUs = useRef(new Set());
  const oursRef = useRef([]);
  const key = backing?.status === "done" ? `${backing.instrumental?.url}|${backing.partner?.url || ""}|${backing.vocals?.url || ""}` : null;

  useEffect(() => {
    if (!key) return undefined;
    let cancelled = false;
    setState("loading");
    (async () => {
      try {
        const inst = await ensureChannel(engine, backing.instrumental.url, `${title} · music`);
        const partner = backing.partner && backing.partner.url !== backing.vocals?.url ? await ensureChannel(engine, backing.partner.url, `${title} · duet partner`) : null;
        const vocals = backing.vocals ? await ensureChannel(engine, backing.vocals.url, `${title} · AI vocal`) : null;
        if (cancelled) return;
        const ours = new Set([inst, partner, vocals].filter((i) => i != null));
        oursRef.current = [...ours];
        engine.channels.forEach((c, i) => {
          if (c?.buffer && !ours.has(i) && !c.muted) { mutedByUs.current.add(i); engine.setChannelMute(i, true); }
        });
        setChannels({ inst, partner, vocals });
        setState("ready");
      } catch (e) {
        if (!cancelled) { setError(e?.message || "Couldn't load the backing track."); setState("error"); }
      }
    })();
    return () => { cancelled = true; };
  }, [key, engine, backing, title]);

  useEffect(() => () => {
    engine.pause();
    // Leave the backing silent so other Creator pages don't play it under their song.
    oursRef.current.forEach((i) => engine.setChannelMute(i, true));
    mutedByUs.current.forEach((i) => engine.setChannelMute(i, false));
    mutedByUs.current.clear();
  }, [engine]);

  // What plays under your voice: music only · music + partner · music + every AI vocal (+ optional quiet guide).
  useEffect(() => {
    if (state !== "ready") return;
    const { inst, partner, vocals } = channels;
    engine.setChannelMute(inst, false);
    engine.setChannelVolume(inst, FULL_VOLUME);
    const partnerIdx = partner != null ? partner : mode === "partner" ? vocals : null;
    if (partner != null) {
      engine.setChannelMute(partner, mode !== "partner");
      engine.setChannelVolume(partner, FULL_VOLUME);
    }
    if (vocals != null) {
      const full = mode === "full" || (mode === "partner" && partnerIdx === vocals);
      engine.setChannelMute(vocals, !(full || guide));
      engine.setChannelVolume(vocals, full ? FULL_VOLUME : GUIDE_VOLUME);
    }
  }, [state, channels, mode, guide, engine]);

  const duration = channels.inst != null ? engine.channels[channels.inst]?.buffer?.duration || 0 : 0;
  return { state, error, channels, duration };
}
